declare var window: any;

/*
Make a list searchable by hiding items that don't match the search term
How to use:
1. Add the hook to the parent/ancestor of the list you want to search
2. Add a `data-search-input` attr to the same element which should contain the id of the search input
3. Optional: Add `data-search-display` attr to the same element to set the display value of the items when shown.
Default is "block".
4. Optional: Add `data-search-list-parent-selector` attr to the same element to select any descendant as the parent of the list.
Default is the element itself.
5. Optional: Add `data-search-list-child-content-selector` attr to the same element to select elements within each child to search against.
The child here is the direct child of the list parent. Default is to search the entire child's text content.
6. Optional: You can clear the search by dispatching a custom event "clear-search" on the search input.

Example:
```
<.text_input id="search-input" ... />

// ul > li content will be searched:
<ul id="my-list" phx-hook="Search" data-search-input="search-input" data-search-display="flex">
  <li>Item 1</li>
  <li>Item 2</li>
  <li>Item 3</li>
</ul>

// tbody > tr content will be searched:
<table id="my-table" phx-hook="Search" data-search-input="search-input" data-search-list-parent-selector="tbody">
  <thead>...</thead>
  <tbody>
    <tr><td>Item 1</td></tr>
    <tr><td>Item 2</td></tr>
    ...
  </tbody>
</table>

// tbody > tr content will be searched, but only the text within the [data-search-content] element:
<table id="my-other-table" phx-hook="Search" data-search-input="search-input" data-search-list-parent-selector="tbody" data-search-list-child-content-selector="[data-search-content]">
  <thead>...</thead>
  <tbody>
    <tr>
      <td>
        Item 1
        <span data-search-content>Some Item 1 content</span>
      </td>
    </tr>
    <tr>
      <td>
        Item 2
        <span data-search-content>Some Item 2 content</span>
      </td>
    </tr>
    ...
  </tbody>
</table>

<button phx-click={JS.dispatch("clear-search", to: "#search-input")}>Clear Search</button>
```
*/
const Search = {
  searchTerm: "",
  searchInput: null,
  listParent: null,
  display: "block",
  runChildrenCallback(parent: any, attribute: string) {
    parent.querySelectorAll(`[${attribute}]`).forEach((el: any) => {
      window.liveSocket.execJS(el, el.getAttribute(`${attribute}`));
    });
  },
  toggleItems(this: any, searchTerm: string) {
    const listParent = this.listParent;
    const listParentChildren = listParent.children;
    const display = this.display;
    // NodeList is not true array, so convert it to array to use forEach
    const children = Array.from(listParentChildren);

    const contentSelector = this.el.getAttribute(
      "data-search-list-child-content-selector",
    );

    children.forEach((child: any) => {
      const contentElement = contentSelector
        ? child.querySelector(contentSelector)
        : child;
      // Skip if content element not found (e.g., selector doesn't match)
      if (!contentElement) return;
      const childText = contentElement.textContent.toLowerCase();

      if (childText.includes(searchTerm.toLowerCase())) {
        // JS encoded from Phoenix.LiveView.JS `JS.show(display: display)`
        window.liveSocket.execJS(
          child,
          `[["show", {"display": "${display}"}]]`,
        );
        this.runChildrenCallback(child, "data-search-on-display");
      } else {
        /*
        JS encoded from Phoenix.LiveView.JS.set_attribute({"style", "display: none"}).
        `JS.hide()` is not used here because it only targets elements that are visible,
        and if an element is already hidden (for example, collapsed), it won't apply the hide
        action again.
        */
        window.liveSocket.execJS(
          child,
          `[["set_attr", {"attr": ["style", "display: none"]}]]`,
        );
        this.runChildrenCallback(child, "data-search-on-hide");
      }
    });
  },
  mounted(this: any) {
    const searchInputId = this.el.getAttribute("data-search-input");
    const searchInput = <HTMLInputElement>(
      document.getElementById(searchInputId)
    );
    this.searchInput = searchInput;

    const listParentSelector = this.el.getAttribute(
      "data-search-list-parent-selector",
    );
    const listParent = this.el.querySelector(listParentSelector);
    this.listParent = listParent || this.el;

    const display = this.el.getAttribute("data-search-display") || "block";
    this.display = display;

    searchInput &&
      searchInput.addEventListener("input", (e: any) => {
        const searchTerm = e.target.value;
        this.searchTerm = searchTerm;
        this.toggleItems(searchTerm);
      });

    searchInput &&
      searchInput.addEventListener("clear-search", () => {
        searchInput.value = "";
        searchInput.dispatchEvent(new Event("input", { bubbles: true }));
      });
  },
  updated(this: any) {
    if (this.searchInput) this.searchInput.value = this.searchTerm;
    this.toggleItems(this.searchTerm);
  },
};

export default Search;
